import { FC, } from "react";
import { Server } from "@hostingbv/lib/src/api/partner/models/Server";
import { useRealtimeDeployments } from "./deployments/RealtimeDeployments";
import { useNavigate } from "react-router-dom";
import { DeploymentStatusLabel } from "@hostingbv/lib/src/components/DeploymentStatusLabel";
import { Card } from "@hostingbv/lib/src/components/Card";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { useWindhostingPHPInfo, useWindhostingWebserverInfo } from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode, faCog } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { useApiClient } from "@hostingbv/lib/src/api";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { config } from "../config";
import { useModal } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { WindhostingWebserverConfigModal } from "../modals/WindhostingWebserverConfigModal";
import { WindhostingPHPINIModal } from "../modals/WindhostingPHPINIModal";
import { WindhostingPHPConfigModal } from "../modals/WindhostingPHPConfigModal";
import { DeploymentHistoryBlock } from "./deployments/DeploymentHistoryBlock";
import { DataTable, navigateRow } from "@ventumsoftware/react-toolkit";

export const WindhostingServerDetails: FC<{ server: Server }> = ({
  server,
}) => {
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)

  return (
    <>
      <DetailsBlock server={server} />
      <SitesBlock server={server} />
      {!server.isShared && (
        <>
          {can('edit', 'servers', server.permissions) ? <>
            <WebserverBlock server={server} />
            <PHPBlockList server={server} />
          </> : <SoftwareBlock server={server} />}
          <DeploymentHistoryBlock entityUUID={server.id} />
        </>
      )}
    </>
  );
};

function DetailsBlock(props: { server: Server }) {
  return <div className={'my-4'}>
    <Card title={'Details'}>
      <div className={"flex"}>
        <dl className={"flex-1"}>
          <dt className={"text-slate-500 text-xs"}>Name</dt>
          <dd className={"text-slate-900 mb-4"}>{props.server.displayName}</dd>
          <dt className={"text-slate-500 text-xs"}>Support</dt>
          <dd className={"text-slate-900 mb-4"}>{props.server.isShared ? "Shared Server" : (props.server.managedServer ? 'Managed Server' : 'Unmanaged Server')}</dd>
        </dl>
      </div>
    </Card>
  </div>
}

const WebserverBlock: FC<{ server: Server }> = ({ server }) => {
  const { data } = useWindhostingWebserverInfo(server.id)

  const { open } = useModal({
    title: "Webserver config", body: <WindhostingWebserverConfigModal server={server} />
  })

  return <Card title={"Webserver"} label={data?.component ?? ""}>
    <div className={"flex items-center"}>
      <div className={"h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center"}>
        <FontAwesomeIcon icon={faCode} className={"text-brand-500"} />
      </div>
      <div className={"flex-1 mx-6"}>
        <h2 className={"font-medium text-lg"}>Webserver Config</h2>
        <p className={"text-slate-800 text-sm max-w-lg"}>
          Make custom changes to the webserver config, beware for breaking changes.
        </p>
      </div>
      <Button type={'secondary'} icon={faCog} size={'sm'} text={'Config'} onClick={() => open()} />
    </div>
  </Card>
}

/** Without edit access the config blocks would be empty, so only show what is installed. */
const SoftwareBlock: FC<{ server: Server }> = ({ server }) => {
  const { data: webserver } = useWindhostingWebserverInfo(server.id)
  const { data: php } = useWindhostingPHPInfo(server.id)

  return <Card title={"Software"}>
    <dl>
      <dt className={"text-slate-500 text-xs"}>Webserver</dt>
      <dd className={"text-slate-900 mb-4"}>{webserver?.component ?? "-"}</dd>
      <dt className={"text-slate-500 text-xs"}>PHP versions</dt>
      <dd className={"text-slate-900"}>
        {php?.phpVersions?.length ? php.phpVersions.map(x => `PHP ${x}`).join(", ") : "-"}
      </dd>
    </dl>
  </Card>
}

const PHPBlockList: FC<{ server: Server }> = ({ server }) => {
  const { data } = useWindhostingPHPInfo(server.id)
  return <>
    {data?.phpVersions?.map(x => <PHPBlock key={x} server={server} version={x} />)}
  </>
}

const PHPBlock: FC<{ server: Server, version: string }> = ({ server, version }) => {
  const { open: openINIModal } = useModal({
    title: "php ini config", body: <WindhostingPHPINIModal server={server} version={version} />
  })

  const { open: openConfigModal } = useModal({
    title: "php config", body: <WindhostingPHPConfigModal server={server} version={version} />
  })

  return <Card title={"PHP"} label={`fpm-${version}`}>
    <div className={"flex items-center"}>
      <div className={"h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center"}>
        <FontAwesomeIcon icon={faCode} className={"text-brand-500"} />
      </div>
      <div className={"flex-1 mx-6"}>
        <h2 className={"font-medium text-lg"}>PHP-FPM INI</h2>
        <p className={"text-slate-800 text-sm max-w-lg"}>
          Make custom changes to the php-fpm ini, beware for breaking changes.
        </p>
      </div>
      <Button type={'secondary'} icon={faCog} size={'sm'} text={'Config'} onClick={() => openINIModal()} />
    </div>

    <hr className="-mx-6 my-4 border-slate-200" />
    <div className={"flex items-center"}>
      <div className={"h-12 w-12 rounded-full bg-brand-50 flex items-center justify-center"}>
        <FontAwesomeIcon icon={faCode} className={"text-brand-500"} />
      </div>
      <div className={"flex-1 mx-6"}>
        <h2 className={"font-medium text-lg"}>PHP-FPM Default Pool Config</h2>
        <p className={"text-slate-800 text-sm max-w-lg"}>
          Make custom changes to the php-fpm config, beware for breaking changes.
        </p>
      </div>
      <Button type={'secondary'} icon={faCog} size={'sm'} text={'Config'} onClick={() => openConfigModal()} />
    </div>
  </Card>
}

function SitesBlock(props: { server: Server }) {
  const { getRealtimeStatus } = useRealtimeDeployments()!
  const navigate = useNavigate()
  return <div className="my-4">
    <SectionHeader>Sites on this server</SectionHeader>
    <DataTable
      keyProperty={'id'}
      data={props.server.sites}
      columns={[
        {
          header: 'Name',
          property: 'displayName',
        },
        {
          header: 'Customer',
          property: 'customer',
          transform: (customer) => customer?.displayName ?? 'n/a',
        },
        {
          header: 'Deployment status',
          property: 'deploymentStatus',
          transform: (status, row) => <DeploymentStatusLabel status={getRealtimeStatus(row.id, status)} />
        },
      ]}
      onClickRow={(row, e) => navigateRow(navigate, `/sites/${row.id}`, e)}
    />
  </div>
}
